import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import HistoryOutlinedIcon from '@material-ui/icons/HistoryOutlined';
import SettingsBackupRestoreIcon from '@material-ui/icons/SettingsBackupRestore';
import {
    Checkbox,
    CircularProgress,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Tooltip,
} from '@material-ui/core';

import { checkConditions } from '../../../TrackerViewerV2/utils';
import FieldHistoryDialog from '../FieldHistoryDialog';
import {
    areCellValuesEqual,
    formatTableCellDisplayValue,
    getCellFieldKey,
    getColumnDisplayLabel,
    getEditingCellAttr,
    getFieldHistoryKey,
    getNonEditableValidationMessage,
    getStickyOrderedColumns,
    getTierDateMessageForField,
    getTierDateValidationMode,
    getVisibleColumnHeaders,
    isColumnNonEditable,
    isColumnSticky,
    isHistoryEnabledColumn,
    isIgnoredClickAwayTarget,
    isEscapeKeyEvent,
    isTierResetEnabledForField,
    normalizeSelectSaveValue,
    resolveColumnConfig,
    shouldAutoSaveOnChange,
    shouldDeferInlineEditEscapeCancel,
} from '../../utils';
import {
    getBelowTrackerThresholdError,
    getIdmTierRestrictionMessage,
    isFieldEditableForRowTier,
    shouldBlockIdmTriggerFieldSave,
} from '../../idmTierRules';
import { isConfigEnabled } from '../../config/isConfigEnabled';
import InlineEditableCell from './InlineEditableCell';
import useStyles, { CHECKBOX_COLUMN_WIDTH, STICKY_COLUMN_WIDTH } from './style';

const EllipsisWithTooltip = ({ text, className }) => {
    const displayText = text ?? '';
    const tooltipTitle = displayText === '' || displayText === '—' ? '' : String(displayText);

    if (!tooltipTitle) {
        return <span className={className}>{displayText}</span>;
    }

    return (
        <Tooltip title={tooltipTitle} arrow placement="top">
            <span className={className}>{displayText}</span>
        </Tooltip>
    );
};

const BELOW_THRESHOLD_TOAST_ID = 'idm-below-tracker-threshold';

const showBelowThresholdToast = (row, behaviorConfig) => {
    const error = getBelowTrackerThresholdError(row, behaviorConfig);
    if (!error) {
        return;
    }

    toast.error(
        <>
            <strong>{error.title}</strong>
            <div>{error.message}</div>
        </>,
        { toastId: BELOW_THRESHOLD_TOAST_ID },
    );
};

const AdminEditableTable = (props) => {
    const {
        rows = [],
        blockHeaders = [],
        loading,
        loader,
        page,
        rowsPerPage,
        totalRecords,
        onPageChange,
        onRowsPerPageChange,
        onSaveCell,
        isRowSaving,
        hasCheckbox = false,
        selectedDataModel,
        selectedDataElement = [],
        sort = {},
        onSortChange,
        behaviorConfig,
        tierResetConfig = [],
        onTierReset,
        resettingRowId = null,
        isTierResetLoading = false,
    } = props;

    const classes = useStyles();
    const inputClasses = { input: classes.fieldLabel };
    const [selectedRowIds, setSelectedRowIds] = useState([]);
    const [editingCell, setEditingCell] = useState(null);
    const [cellDraft, setCellDraft] = useState(null);
    const [fieldHistoryOpen, setFieldHistoryOpen] = useState({
        open: false,
        label: '',
        history: [],
        isDate: false,
    });

    const visibleBlockHeaders = useMemo(
        () => getStickyOrderedColumns(getVisibleColumnHeaders(blockHeaders)),
        [blockHeaders],
    );

    /** `left` offset per column, indexed by position in `visibleBlockHeaders`; `null` for non-sticky columns. */
    const stickyColumnLeftOffsets = useMemo(() => {
        let cumulativeLeft = hasCheckbox ? CHECKBOX_COLUMN_WIDTH : 0;

        return visibleBlockHeaders.map((column) => {
            if (!isColumnSticky(column)) {
                return null;
            }

            const left = cumulativeLeft;
            cumulativeLeft += STICKY_COLUMN_WIDTH;
            return left;
        });
    }, [visibleBlockHeaders, hasCheckbox]);

    const editingCellRef = useRef(null);
    const cellDraftRef = useRef(null);
    const commitCellEditRef = useRef(null);
    const cancelCellEditRef = useRef(null);
    const isSelectMenuOpenRef = useRef(false);

    const clearCellEdit = useCallback(() => {
        isSelectMenuOpenRef.current = false;
        setEditingCell(null);
        setCellDraft(null);
        editingCellRef.current = null;
        cellDraftRef.current = null;
    }, []);

    /** Discards the active inline edit without saving changes. */
    const cancelCellEdit = useCallback(() => {
        clearCellEdit();
    }, [clearCellEdit]);

    useEffect(() => {
        editingCellRef.current = editingCell;
    }, [editingCell]);

    useEffect(() => {
        cellDraftRef.current = cellDraft;
    }, [cellDraft]);

    useEffect(() => {
        clearCellEdit();
    }, [page, rowsPerPage, loading, sort, clearCellEdit]);

    const isTriggerFieldSaveBlocked = useCallback((row, fieldKey) => (
        shouldBlockIdmTriggerFieldSave(row, fieldKey, behaviorConfig)
    ), [behaviorConfig]);

    const visibleRowIds = useMemo(
        () => rows.map((row) => row._id).filter(Boolean),
        [rows],
    );

    const isAllSelected = visibleRowIds.length > 0
        && visibleRowIds.every((id) => selectedRowIds.includes(id));

    const isIndeterminate = selectedRowIds.length > 0 && !isAllSelected;

    const commitCellEdit = useCallback(() => {
        const activeEdit = editingCellRef.current;
        const draftRow = cellDraftRef.current;

        if (!activeEdit || !draftRow) {
            return;
        }

        const sourceRow = rows.find((row) => row._id === activeEdit.rowId);
        if (!sourceRow) {
            clearCellEdit();
            return;
        }

        const fieldKey = activeEdit.fieldKey;
        const nextValue = draftRow[fieldKey];
        const previousValue = sourceRow[fieldKey];

        if (isTriggerFieldSaveBlocked(draftRow, fieldKey)) {
            showBelowThresholdToast(draftRow, behaviorConfig);
            clearCellEdit();
            return;
        }

        clearCellEdit();

        if (!areCellValuesEqual(previousValue, nextValue)) {
            onSaveCell?.(draftRow, fieldKey, nextValue);
        }
    }, [rows, onSaveCell, clearCellEdit, isTriggerFieldSaveBlocked, behaviorConfig]);

    useEffect(() => {
        commitCellEditRef.current = commitCellEdit;
    }, [commitCellEdit]);

    useEffect(() => {
        cancelCellEditRef.current = cancelCellEdit;
    }, [cancelCellEdit]);

    const syncSelectMenuOpenState = useCallback(() => {
        window.setTimeout(() => {
            const hasOpenListbox = Boolean(document.querySelector('[role="listbox"]'));
            const hasOpenApiMenu = Boolean(document.querySelector('.api-select-menu-panel'));
            if (!hasOpenListbox && !hasOpenApiMenu) {
                isSelectMenuOpenRef.current = false;
            }
        }, 0);
    }, []);

    const handleInlineEditEscape = useCallback((event) => {
        if (!isEscapeKeyEvent(event) || !editingCellRef.current) {
            return;
        }

        const target = event.target instanceof Element ? event.target : null;
        const hasOpenSelectOverlay = Boolean(
            document.querySelector('[role="listbox"]')
            || document.querySelector('.api-select-menu-panel'),
        );

        if (isSelectMenuOpenRef.current && !hasOpenSelectOverlay) {
            isSelectMenuOpenRef.current = false;
        }

        if (shouldDeferInlineEditEscapeCancel(target, isSelectMenuOpenRef.current)) {
            syncSelectMenuOpenState();
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        cancelCellEditRef.current?.();
    }, [syncSelectMenuOpenState]);

    useEffect(() => {
        if (!editingCell) {
            return undefined;
        }

        const editingCellSelector = `[data-editing-cell="${getEditingCellAttr(editingCell.rowId, editingCell.fieldKey)}"]`;

        const resolveTarget = (target) => (target instanceof Element ? target : null);

        const commitIfOutside = (target) => {
            if (isSelectMenuOpenRef.current) {
                return;
            }

            const element = resolveTarget(target);
            if (!element) {
                return;
            }

            const activeCell = document.querySelector(editingCellSelector);
            if (activeCell?.contains(element)) {
                return;
            }

            if (isIgnoredClickAwayTarget(element)) {
                return;
            }

            const activeElement = document.activeElement;
            if (
                activeCell
                && activeElement instanceof HTMLElement
                && activeCell.contains(activeElement)
            ) {
                activeElement.blur();
            }

            commitCellEditRef.current?.();
        };

        const handlePointerDown = (event) => {
            commitIfOutside(event.target);
        };

        const handleFocusIn = (event) => {
            commitIfOutside(event.target);
        };

        const handleKeyDown = (event) => {
            handleInlineEditEscape(event);
        };

        document.addEventListener('mousedown', handlePointerDown, true);
        document.addEventListener('focusin', handleFocusIn);
        document.addEventListener('keydown', handleKeyDown, true);

        return () => {
            document.removeEventListener('mousedown', handlePointerDown, true);
            document.removeEventListener('focusin', handleFocusIn);
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [editingCell, handleInlineEditEscape]);

    const startCellEdit = useCallback((row, column) => {
        const fieldKey = getCellFieldKey(column);

        if (
            !isConfigEnabled(behaviorConfig?.features?.inlineEdit)
            || column?.readOnly
            || isColumnNonEditable(column)
            || !isFieldEditableForRowTier(fieldKey, column, row, selectedDataElement, behaviorConfig)
            || isRowSaving
        ) {
            return;
        }
        const activeEdit = editingCellRef.current;

        if (activeEdit?.rowId === row._id && activeEdit?.fieldKey === fieldKey) {
            return;
        }

        if (activeEdit) {
            commitCellEdit();
        }

        const draftRow = JSON.parse(JSON.stringify(row));
        setEditingCell({ rowId: row._id, fieldKey });
        setCellDraft(draftRow);
        editingCellRef.current = { rowId: row._id, fieldKey };
        cellDraftRef.current = draftRow;
    }, [commitCellEdit, isRowSaving, selectedDataElement, behaviorConfig]);

    const handleCellChange = useCallback((key, value, _type, _fieldType, element) => {
        const draftRow = cellDraftRef.current;
        if (!draftRow) {
            return;
        }

        const column = resolveColumnConfig(key, blockHeaders, selectedDataElement) || element;
        const normalizedValue = normalizeSelectSaveValue(value, column);

        const updatedRow = {
            ...draftRow,
            [key]: normalizedValue,
        };

        setCellDraft(updatedRow);
        cellDraftRef.current = updatedRow;

        if (column && shouldAutoSaveOnChange(column)) {
            const sourceRow = rows.find((row) => row._id === updatedRow._id);
            const previousValue = sourceRow?.[key];

            if (isTriggerFieldSaveBlocked(updatedRow, key)) {
                showBelowThresholdToast(updatedRow, behaviorConfig);
                clearCellEdit();
                return;
            }

            clearCellEdit();

            if (!areCellValuesEqual(previousValue, normalizedValue)) {
                onSaveCell?.(updatedRow, key, normalizedValue);
            }
        }
    }, [
        blockHeaders,
        rows,
        onSaveCell,
        clearCellEdit,
        selectedDataElement,
        isTriggerFieldSaveBlocked,
        behaviorConfig,
    ]);

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedRowIds(visibleRowIds);
            return;
        }
        setSelectedRowIds([]);
    };

    const handleSelectRow = (event, rowId) => {
        event.stopPropagation();
        if (event.target.checked) {
            setSelectedRowIds((prev) => [...prev, rowId]);
            return;
        }
        setSelectedRowIds((prev) => prev.filter((id) => id !== rowId));
    };

    const isColumnVisibleForRow = (column, row) => checkConditions(column?.conditions, row);

    const getCellClassName = (column, isEditable) => {
        const fieldKey = getCellFieldKey(column);
        const classNames = [classes.tableBodyCell];

        if (fieldKey === 'originalName' || fieldKey === 'fileName' || column.fullWidth) {
            classNames.push(classes.fileNameCell);
        }
        if (isEditable) {
            classNames.push(classes.editableCell);
        }

        return classNames.join(' ');
    };

    const colSpan = visibleBlockHeaders.length + (hasCheckbox ? 1 : 0);

    const handleShowFieldHistory = useCallback((column, row) => {
        const historyKey = getFieldHistoryKey(column);
        if (!historyKey) {
            return;
        }

        setFieldHistoryOpen({
            open: true,
            label: column.label || getCellFieldKey(column),
            history: row?.[historyKey] ?? [],
            isDate: column?.type === 'date' || column?.type === 'datetime',
        });
    }, []);

    const handleCloseFieldHistory = useCallback(() => {
        setFieldHistoryOpen({
            open: false,
            label: '',
            history: [],
            isDate: false,
        });
    }, []);

    const handleTierResetClick = useCallback((row) => {
        const rowId = row?._id;
        if (!rowId) {
            return;
        }

        const activeEdit = editingCellRef.current;
        if (activeEdit?.rowId === rowId) {
            toast.error('Save your changes first before reset document');
            return;
        }

        onTierReset?.(row);
    }, [onTierReset]);

    const handleSelectMenuOpen = useCallback(() => {
        isSelectMenuOpenRef.current = true;
    }, []);

    const handleSelectMenuClose = useCallback(() => {
        isSelectMenuOpenRef.current = false;
    }, []);

    const fieldProps = useMemo(() => ({
        ...props,
        appId: props.appId,
        selectedDataModel,
        isInlineTableEdit: true,
        menuPlacement: 'auto',
        onSelectMenuOpen: handleSelectMenuOpen,
        onSelectMenuClose: handleSelectMenuClose,
    }), [props, selectedDataModel, handleSelectMenuOpen, handleSelectMenuClose]);

    const blockPaginationIfEditing = useCallback(() => {
        if (editingCellRef.current || isRowSaving) {
            toast.error(
                behaviorConfig?.table?.inlineEdit?.blockPaginationMessage
                    || 'Please save your changes before navigating to the next page.',
                { toastId: 'inline-edit-block-pagination' },
            );
            return true;
        }

        return false;
    }, [isRowSaving, behaviorConfig]);

    const handlePageChange = useCallback((event, nextPage) => {
        if (blockPaginationIfEditing()) {
            return;
        }

        onPageChange?.(event, nextPage);
    }, [blockPaginationIfEditing, onPageChange]);

    const handleRowsPerPageChange = useCallback((event) => {
        if (blockPaginationIfEditing()) {
            return;
        }

        onRowsPerPageChange?.(event);
    }, [blockPaginationIfEditing, onRowsPerPageChange]);

    const handleSort = useCallback((fieldKey) => {
        if (!onSortChange || !fieldKey) {
            return;
        }

        const currentDirection = sort?.[fieldKey];
        let nextSort = {};

        if (!currentDirection) {
            nextSort = { [fieldKey]: 1 };
        } else if (currentDirection === 1) {
            nextSort = { [fieldKey]: -1 };
        }

        onSortChange(nextSort);
    }, [onSortChange, sort]);

    const getSortDirection = (fieldKey) => {
        const direction = sort?.[fieldKey];
        if (direction === 1) {
            return 'asc';
        }
        if (direction === -1) {
            return 'desc';
        }
        return false;
    };

    return (
        <Paper className={classes.tableWrapper} elevation={0}>
            <TableContainer className={classes.tableContainer}>
                <Table size="small" className={classes.table}>
                    <TableHead className={classes.tableHead}>
                        <TableRow className={classes.headerRow}>
                            {hasCheckbox && (
                                <TableCell
                                    padding="checkbox"
                                    className={classes.checkboxHeadCell}
                                >
                                    <Checkbox
                                        indeterminate={isIndeterminate}
                                        checked={isAllSelected}
                                        onChange={handleSelectAll}
                                        color="primary"
                                        size="small"
                                    />
                                </TableCell>
                            )}
                            {visibleBlockHeaders.map((column, columnIndex) => {
                                const fieldKey = getCellFieldKey(column);
                                const headerLabel = column.label || column.value;
                                const isSortActive = Boolean(sort?.[fieldKey]);
                                const sortDirection = getSortDirection(fieldKey);
                                const isSortable = Boolean(onSortChange) && column.sort !== false;
                                const stickyLeft = stickyColumnLeftOffsets[columnIndex];
                                const isStickyColumn = stickyLeft != null;
                                const headCellClassName = isStickyColumn
                                    ? `${classes.tableHeadCell} ${classes.stickyColumnHeadCell}`
                                    : classes.tableHeadCell;

                                return (
                                    <TableCell
                                        key={fieldKey}
                                        className={headCellClassName}
                                        style={isStickyColumn ? { left: stickyLeft } : undefined}
                                        sortDirection={sortDirection || false}
                                    >
                                        {isSortable ? (
                                            <TableSortLabel
                                                active={isSortActive}
                                                direction={sortDirection || 'asc'}
                                                onClick={() => handleSort(fieldKey)}
                                                className={classes.sortLabel}
                                            >
                                                <EllipsisWithTooltip
                                                    text={headerLabel}
                                                    className={classes.headerText}
                                                />
                                            </TableSortLabel>
                                        ) : (
                                            <EllipsisWithTooltip
                                                text={headerLabel}
                                                className={classes.headerText}
                                            />
                                        )}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={colSpan} className={classes.emptyCell}>
                                    {loader}
                                </TableCell>
                            </TableRow>
                        ) : !rows.length ? (
                            <TableRow>
                                <TableCell colSpan={colSpan} className={classes.emptyCell}>
                                    No data available
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((row) => {
                                const rowId = row._id;
                                const isSelected = selectedRowIds.includes(rowId);
                                const isRowEditing = editingCell?.rowId === rowId;

                                return (
                                    <TableRow
                                        hover
                                        key={rowId}
                                        className={`${classes.tableBodyRow} ${isRowEditing ? classes.editingRow : ''}`}
                                    >
                                        {hasCheckbox && (
                                            <TableCell
                                                padding="checkbox"
                                                className={classes.checkboxBodyCell}
                                            >
                                                <Checkbox
                                                    checked={isSelected}
                                                    onChange={(event) => handleSelectRow(event, rowId)}
                                                    color="primary"
                                                    size="small"
                                                />
                                            </TableCell>
                                        )}
                                        {visibleBlockHeaders.map((column, columnIndex) => {
                                            const fieldKey = getCellFieldKey(column);
                                            const stickyLeft = stickyColumnLeftOffsets[columnIndex];
                                            const isStickyColumn = stickyLeft != null;
                                            const stickyColumnClassName = isStickyColumn
                                                ? classes.stickyColumnBodyCell
                                                : '';
                                            const stickyColumnStyle = isStickyColumn ? { left: stickyLeft } : undefined;
                                            const columnConfig = resolveColumnConfig(
                                                fieldKey,
                                                blockHeaders,
                                                selectedDataElement,
                                            ) || column;
                                            const isVisible = isColumnVisibleForRow(columnConfig, row);
                                            const isTierEditable = isFieldEditableForRowTier(
                                                fieldKey,
                                                columnConfig,
                                                row,
                                                selectedDataElement,
                                                behaviorConfig,
                                            );
                                            const isEditable = isConfigEnabled(behaviorConfig?.features?.inlineEdit)
                                                && !columnConfig.readOnly
                                                && !isColumnNonEditable(columnConfig)
                                                && isTierEditable;
                                            const isEditing = editingCell?.rowId === rowId
                                                && editingCell?.fieldKey === fieldKey;
                                            const showHistory = isHistoryEnabledColumn(columnConfig, behaviorConfig);
                                            const showTierReset = isConfigEnabled(behaviorConfig?.tier?.reset)
                                                && isTierResetEnabledForField(
                                                    fieldKey,
                                                    row,
                                                    tierResetConfig,
                                                    columnConfig,
                                                );
                                            const actionIconCount = (showHistory ? 1 : 0) + (showTierReset ? 1 : 0);
                                            const cellValueClassName = actionIconCount === 2
                                                ? `${classes.cellValue} ${classes.cellValueWithTwoActions}`
                                                : actionIconCount === 1
                                                    ? `${classes.cellValue} ${classes.cellValueWithOneAction}`
                                                    : classes.cellValue;
                                            const isRowTierResetting = isTierResetLoading
                                                && String(resettingRowId) === String(rowId);
                                            const rowForDateValidation = (
                                                editingCell?.rowId === rowId && cellDraft
                                            ) ? cellDraft : row;
                                            const dateFieldMessage = getTierDateMessageForField(
                                                rowForDateValidation,
                                                fieldKey,
                                                behaviorConfig,
                                            );
                                            const dateValidationMode = getTierDateValidationMode(behaviorConfig);
                                            const hasDateFieldMessage = Boolean(dateFieldMessage);

                                            if (!isVisible) {
                                                return (
                                                    <TableCell key={fieldKey} className={`${classes.tableBodyCell} ${stickyColumnClassName}`} style={stickyColumnStyle}>
                                                        <EllipsisWithTooltip text="—" className={classes.cellValue} />
                                                    </TableCell>
                                                );
                                            }

                                            return (
                                                <TableCell
                                                    key={fieldKey}
                                                    data-editing-cell={isEditing ? getEditingCellAttr(rowId, fieldKey) : undefined}
                                                    className={`${getCellClassName(column, isEditable)} ${stickyColumnClassName} ${isEditing ? classes.editCell : ''} ${hasDateFieldMessage ? classes.tableBodyCellWithMessage : ''}`}
                                                    style={stickyColumnStyle}
                                                    onKeyDownCapture={isEditing ? handleInlineEditEscape : undefined}
                                                    onMouseDown={(event) => {
                                                        if (isEditing) {
                                                            event.stopPropagation();
                                                        }
                                                    }}
                                                    onDoubleClick={(event) => {
                                                        if (isRowSaving || isEditing) {
                                                            return;
                                                        }

                                                        if (isColumnNonEditable(columnConfig)) {
                                                            event.stopPropagation();
                                                            if (isConfigEnabled(behaviorConfig?.validation?.nonEditable)) {
                                                                toast.info(getNonEditableValidationMessage(
                                                                    columnConfig,
                                                                    behaviorConfig,
                                                                ));
                                                            }
                                                            return;
                                                        }

                                                        if (!isTierEditable) {
                                                            event.stopPropagation();
                                                            toast.info(getIdmTierRestrictionMessage(
                                                                columnConfig,
                                                                row,
                                                                selectedDataElement,
                                                                behaviorConfig,
                                                            ));
                                                            return;
                                                        }

                                                        if (!columnConfig.readOnly) {
                                                            event.preventDefault();
                                                            event.stopPropagation();
                                                            startCellEdit(row, columnConfig);
                                                        }
                                                    }}
                                                >
                                                    <div className={classes.cellStack}>
                                                        {isEditing ? (
                                                            <InlineEditableCell
                                                                column={columnConfig}
                                                                dataObj={cellDraft}
                                                                onChange={handleCellChange}
                                                                inputClasses={inputClasses}
                                                                editorClassName={classes.cellEditor}
                                                                fieldProps={fieldProps}
                                                            />
                                                        ) : (
                                                            <span className={classes.cellContent}>
                                                                <EllipsisWithTooltip
                                                                    text={formatTableCellDisplayValue(row[fieldKey], columnConfig)}
                                                                    className={cellValueClassName}
                                                                />
                                                                {showHistory ? (
                                                                    <Tooltip title="View field history" arrow>
                                                                        <IconButton
                                                                            size="small"
                                                                            onMouseDown={(event) => event.stopPropagation()}
                                                                            onDoubleClick={(event) => event.stopPropagation()}
                                                                            onClick={(event) => {
                                                                                event.stopPropagation();
                                                                                handleShowFieldHistory(columnConfig, row);
                                                                            }}
                                                                            className={classes.historyButton}
                                                                            aria-label={`View ${getColumnDisplayLabel(columnConfig)} history`}
                                                                        >
                                                                            <HistoryOutlinedIcon className={classes.historyIcon} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                ) : null}
                                                                {showTierReset ? (
                                                                    <Tooltip title="Reset tier data" arrow>
                                                                        <span>
                                                                            <IconButton
                                                                                size="small"
                                                                                onMouseDown={(event) => event.stopPropagation()}
                                                                                onDoubleClick={(event) => event.stopPropagation()}
                                                                                onClick={(event) => {
                                                                                    event.stopPropagation();
                                                                                    handleTierResetClick(row);
                                                                                }}
                                                                                className={classes.resetButton}
                                                                                disabled={isRowTierResetting}
                                                                                aria-label={`Reset ${getColumnDisplayLabel(columnConfig)}`}
                                                                            >
                                                                                {isRowTierResetting ? (
                                                                                    <CircularProgress
                                                                                        size={14}
                                                                                        thickness={5}
                                                                                        className={classes.resetSpinner}
                                                                                    />
                                                                                ) : (
                                                                                    <SettingsBackupRestoreIcon className={classes.resetIcon} />
                                                                                )}
                                                                            </IconButton>
                                                                        </span>
                                                                    </Tooltip>
                                                                ) : null}
                                                            </span>
                                                        )}
                                                        {hasDateFieldMessage ? (
                                                            <EllipsisWithTooltip
                                                                text={dateFieldMessage}
                                                                className={
                                                                    dateValidationMode === 'warning'
                                                                        ? classes.fieldWarning
                                                                        : classes.fieldError
                                                                }
                                                            />
                                                        ) : null}
                                                    </div>
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                className={classes.pagination}
                component="div"
                count={totalRecords || 0}
                page={page}
                onChangePage={handlePageChange}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onChangeRowsPerPage={handleRowsPerPageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 15, 25, 50, 100]}
                labelRowsPerPage="Rows per page:"
            />
            <FieldHistoryDialog
                open={fieldHistoryOpen.open}
                label={fieldHistoryOpen.label}
                history={fieldHistoryOpen.history}
                isDate={fieldHistoryOpen.isDate}
                onClose={handleCloseFieldHistory}
            />
        </Paper>
    );
};

export default AdminEditableTable;
